import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { ColorSwatch, Flex, Grid, GridCol, Stack, Text, Title } from '@mantine/core';
import { useSimulation } from '../../../global/hooks/useSimulation.tsx';
import { sliceShareLabel } from './utils/sliceShareLabel';
import { inject, observer } from 'mobx-react';
import { t } from 'i18next';

const COLORS = ['#1C7ED6', '#74C0FC', '#FFD43B', '#FA5252', '#FF922B', '#868E96'];

const GraphResponse: React.FC = inject()(
    observer(() => {
      const { simulationCurveGraph, activities } = useSimulation();
        const toPieData = (record?: Record<string, number>) =>
            record ? Object.entries(record).map(([name, value]) => ({ name, value })) : [];

        const firstWindowData = useMemo(
            () => toPieData(simulationCurveGraph?.morningReponsePercentages),
            [simulationCurveGraph],
        );
        const secondWindowData = useMemo(
            () => toPieData(simulationCurveGraph?.afternoonReponsePercentages),
            [simulationCurveGraph],
        );

        const pieData = [
            {
                key: 'morningReponsePercentages',
                label: t('table.responseSimulation.firstWindowPercentages'),
                data: firstWindowData,
                show: true,
            },
            {
                key: 'afternoonReponsePercentages',
                label: t('table.responseSimulation.secondWindowPercentages'),
                data: secondWindowData,
                show: activities?.period2Start != null && activities?.period2End != undefined,
            },
        ];
        const hasDataToShow = pieData.some((item) => item.show);

        if (!hasDataToShow) return null;

        const visiblePieData = pieData.filter((d) => d.show);
        const columnSpan = visiblePieData.length === 1 ? 12 : visiblePieData.length === 2 ? 6 : 4;

        const allVisibleEntries = visiblePieData.flatMap((d) => d.data);

        const uniqueLegendEntries = Array.from(new Map(allVisibleEntries.map((entry) => [entry.name, entry])).values());

        const renderLegend = (data: any) => (
            <Flex wrap="wrap" justify="flex-start" style={{ marginTop: 10, gap: '10px' }}>
                {data?.map((entry: { name: string }, index: number) => (
                    <Flex key={`legend-item-${index}`} gap="sm">
                        <ColorSwatch size={16} radius="xs" color={COLORS[index % COLORS.length]} />
                        <Text miw={250}>{t(`table.responseSimulation.${entry.name}`)}</Text>
                    </Flex>
                ))}
            </Flex>
        );

        return (
            <Stack>
                <Grid justify="center">
                    {visiblePieData.map(({ key, label, data }) => (
                        <GridCol span={columnSpan} key={key}>
                            <Stack w={'100%'} styles={{ root: { justifyContent: 'center' } }}>
                                <Title order={5} ta="center">
                                    {label}
                                </Title>
                                <ResponsiveContainer width={'100%'} height={350}>
                                    <PieChart accessibilityLayer={false}>
                                        <Pie
                                            data={data}
                                            dataKey="value"
                                            nameKey="name"
                                            rootTabIndex={-1}
                                            innerRadius={30}
                                            outerRadius={100}
                                            label={({ percent }) => ` ${sliceShareLabel(percent)}`}
                                        >
                                            {data.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={COLORS[index % COLORS.length]}
                                                    name={t(`table.responseSimulation.${entry.name}`)}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name) => [` ${Math.round((Number(value) * 100))}%`, name]}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Stack>
                        </GridCol>
                    ))}
                </Grid>
                {renderLegend(uniqueLegendEntries)}
            </Stack>
        );
    }),
);

export default GraphResponse;
